/**
 * Skaper – sosiale-medier abonnenter (manuell registrering).
 *
 * URL: /skaper/sosial-abonnenter
 * Tilgang: skapere (backend håndhever)
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  ArrowLeft, Plus, Trash2, Pencil, Search, X, Bell,
  Wallet, Users as UsersIcon, Clock, AlertTriangle, Layers,
} from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import api, { formatApiError } from "../lib/api";

const CURRENCIES = ["NOK", "EUR", "USD", "SEK", "DKK", "GBP"];
const STATUS_LABELS = {
  active: "Aktiv",
  expiring_soon: "Utløper snart",
  expired: "Utløpt",
  lifetime: "Livstid",
};
const STATUS_STYLE = {
  active:         "text-emerald-400 border-emerald-500/30 bg-emerald-500/10",
  expiring_soon:  "text-amber-400 border-amber-500/40 bg-amber-500/10",
  expired:        "text-rose-400 border-rose-500/40 bg-rose-500/10",
  lifetime:       "text-violet-300 border-violet-500/40 bg-violet-500/10",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("nb-NO", { year: "numeric", month: "short", day: "numeric" }); }
  catch { return "—"; }
}
function fmtMoney(n, currency = "NOK") {
  if (n == null) return "—";
  try { return new Intl.NumberFormat("nb-NO", { style: "currency", currency, maximumFractionDigits: 0 }).format(n); }
  catch { return `${n} ${currency}`; }
}

function daysFromNow(days) {
  const d = new Date();
  d.setDate(d.getDate() + Number(days || 0));
  return d.toISOString();
}

/* ----------------------------------------------------------- STEG 1: velg plan */
function ChoosePlanStep({ platforms, plans, onPick, onCancel, onCustom }) {
  const [platform, setPlatform] = useState(platforms[0] || "Snapchat");
  const [platformAccount, setPlatformAccount] = useState("");

  const accounts = useMemo(() => {
    const set = new Set();
    for (const p of plans) if (p.platform === platform) set.add(p.platform_account || "");
    return Array.from(set);
  }, [plans, platform]);

  const filtered = useMemo(() => {
    return plans.filter((p) =>
      p.platform === platform &&
      (platformAccount === "" || (p.platform_account || "") === platformAccount) &&
      p.active !== false
    );
  }, [plans, platform, platformAccount]);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-[var(--noir-elev)] border border-white/10 w-full sm:max-w-2xl sm:rounded-lg overflow-y-auto max-h-[95vh]">
        <div className="p-4 border-b border-white/10 flex items-center justify-between sticky top-0 bg-[var(--noir-elev)] z-10">
          <h3 className="font-fraunces italic text-xl text-[var(--cream)]">Registrer betaling</h3>
          <button onClick={onCancel} className="btn-ghost p-1.5"><X className="h-4 w-4"/></button>
        </div>

        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.15em] text-[var(--cream-soft)]">Plattform</span>
              <select value={platform} onChange={(e)=>{ setPlatform(e.target.value); setPlatformAccount(""); }}
                className="input-editorial w-full mt-1" data-testid="pick-platform">
                {platforms.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.15em] text-[var(--cream-soft)]">Ditt brukernavn</span>
              <select value={platformAccount} onChange={(e)=>setPlatformAccount(e.target.value)}
                className="input-editorial w-full mt-1" data-testid="pick-platform-account">
                <option value="">Alle</option>
                {accounts.map(a => <option key={a} value={a}>{a || "(uten brukernavn)"}</option>)}
              </select>
            </label>
          </div>

          <div className="text-[var(--cream-soft)] text-sm mt-1">
            Velg en plan for {platform}{platformAccount ? ` · ${platformAccount}` : ""}:
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {filtered.map((p) => (
              <button key={p.id} onClick={() => onPick(p)}
                className="text-left p-3 border border-white/10 bg-[var(--noir)] hover:border-[var(--magenta)]/60 transition-colors"
                data-testid={`pick-plan-${p.id}`}>
                <div className="font-fraunces italic text-lg text-[var(--cream)]">{p.name}</div>
                {p.description && <div className="text-xs text-[var(--cream-soft)] mt-0.5">{p.description}</div>}
                <div className="mt-2 space-y-0.5">
                  {p.periods.map((per) => (
                    <div key={per.id} className="flex justify-between text-xs font-manrope">
                      <span className="text-[var(--cream-soft)]">{per.label} ({per.days}d)</span>
                      <span className="text-[var(--cream)]">{per.price} {p.currency}</span>
                    </div>
                  ))}
                </div>
              </button>
            ))}
            <button onClick={() => onCustom(platform, platformAccount)}
              className="text-left p-3 border border-dashed border-white/20 bg-transparent hover:border-[var(--magenta)]/60"
              data-testid="pick-plan-custom">
              <div className="font-fraunces italic text-lg text-[var(--cream)]">Egendefinert</div>
              <div className="text-xs text-[var(--cream-soft)]">Uten forhåndsdefinert plan — fyll inn beløp og periode manuelt.</div>
            </button>
          </div>

          {filtered.length === 0 && (
            <div className="text-center text-[var(--cream-faint)] text-sm py-3">
              Ingen planer registrert her ennå. <Link to="/skaper/sosial-abonnenter/planer" className="text-[var(--magenta)] underline">Opprett en plan</Link>{" "}
              eller bruk «Egendefinert».
            </div>
          )}
        </div>

        <div className="p-4 border-t border-white/10 sticky bottom-0 bg-[var(--noir-elev)]">
          <button onClick={onCancel} className="btn-ghost w-full">Avbryt</button>
        </div>
      </div>
    </div>
  );
}

/* ----------------------------------------------------------- STEG 2: registrer detaljer */
function DetailStep({ initial, plan, platform, platformAccount, onSave, onCancel, onBack }) {
  const isEdit = !!initial;
  const [f, setF] = useState(() => {
    if (isEdit) {
      return {
        subscriber_handle: initial.subscriber_handle || "",
        subscriber_note: initial.subscriber_note || "",
        currency: initial.currency || "NOK",
        amount: String(initial.amount ?? ""),
        paid_at: (initial.paid_at || new Date().toISOString()).slice(0, 10),
        access_until: (initial.access_until || "").slice(0, 10),
        remind_days_before: initial.remind_days_before ?? 3,
        period_label_snapshot: initial.period_label_snapshot || "",
        selected_period_id: null,
        custom_days: "",
        custom_price: "",
        reminders: (initial.reminders || []).map((r) => ({
          id: r.id,
          remind_at: (r.remind_at || "").slice(0, 10),
          note: r.note || "",
        })),
      };
    }
    const firstPeriod = plan?.periods?.[0];
    return {
      subscriber_handle: "",
      subscriber_note: "",
      currency: plan?.currency || "NOK",
      amount: firstPeriod ? String(firstPeriod.price) : "",
      paid_at: new Date().toISOString().slice(0, 10),
      access_until: firstPeriod ? daysFromNow(firstPeriod.days).slice(0, 10) : "",
      remind_days_before: 3,
      period_label_snapshot: firstPeriod?.label || "",
      selected_period_id: firstPeriod?.id || null,
      custom_days: "",
      custom_price: "",
      reminders: [],
    };
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const pickPeriod = (per) => {
    setF((s) => ({
      ...s,
      selected_period_id: per.id,
      amount: String(per.price),
      access_until: daysFromNow(per.days).slice(0, 10),
      period_label_snapshot: per.label,
      custom_days: "",
      custom_price: "",
    }));
  };

  const pickCustom = () => {
    setF((s) => ({
      ...s,
      selected_period_id: "custom",
      period_label_snapshot: "Egendefinert",
    }));
  };

  const applyCustom = (days, price) => {
    setF((s) => ({
      ...s,
      custom_days: days,
      custom_price: price,
      amount: price !== "" ? String(price) : s.amount,
      access_until: days ? daysFromNow(days).slice(0, 10) : s.access_until,
    }));
  };

  const addReminder = () => {
    setF((s) => ({
      ...s,
      reminders: [
        ...s.reminders,
        { id: undefined, remind_at: new Date(Date.now() + 3 * 86400000).toISOString().slice(0, 10), note: "" },
      ],
    }));
  };
  const rmReminder = (i) => setF((s) => ({ ...s, reminders: s.reminders.filter((_, j) => j !== i) }));
  const setReminder = (i, k, v) => setF((s) => ({
    ...s, reminders: s.reminders.map((r, j) => j === i ? { ...r, [k]: v } : r),
  }));

  const submit = async () => {
    if (!f.subscriber_handle.trim()) return toast.error("Abonnent-handle er påkrevd");
    if (f.amount === "" || Number(f.amount) < 0) return toast.error("Beløp må være ≥ 0");
    setSaving(true);
    const payload = {
      platform,
      platform_account: platformAccount || null,
      subscriber_handle: f.subscriber_handle.trim(),
      subscriber_note: f.subscriber_note.trim() || null,
      amount: Number(f.amount),
      currency: f.currency,
      paid_at: f.paid_at ? new Date(f.paid_at).toISOString() : null,
      access_until: f.access_until ? new Date(f.access_until).toISOString() : null,
      remind_days_before: Number(f.remind_days_before) || 0,
      plan_id: plan?.id || null,
      period_label_snapshot: f.period_label_snapshot || null,
      reminders: f.reminders
        .filter((r) => r.remind_at)
        .map((r) => ({
          id: r.id,
          remind_at: new Date(r.remind_at).toISOString(),
          note: (r.note || "").trim() || null,
        })),
    };
    try { await onSave(payload); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-[var(--noir-elev)] border border-white/10 w-full sm:max-w-xl sm:rounded-lg overflow-y-auto max-h-[95vh]">
        <div className="p-4 border-b border-white/10 flex items-center justify-between sticky top-0 bg-[var(--noir-elev)] z-10">
          <div className="flex items-center gap-2">
            {!isEdit && (
              <button onClick={onBack} className="btn-ghost p-1.5" title="Tilbake"><ArrowLeft className="h-4 w-4"/></button>
            )}
            <h3 className="font-fraunces italic text-xl text-[var(--cream)]">
              {isEdit ? "Rediger" : `${platform}${platformAccount ? " · " + platformAccount : ""}${plan ? " · " + plan.name : ""}`}
            </h3>
          </div>
          <button onClick={onCancel} className="btn-ghost p-1.5"><X className="h-4 w-4"/></button>
        </div>

        <div className="p-4 space-y-3">
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.15em] text-[var(--cream-soft)]">Abonnent (deres handle/navn)*</span>
            <input value={f.subscriber_handle} onChange={(e)=>set("subscriber_handle", e.target.value)}
              placeholder="@kunden" className="input-editorial w-full mt-1" data-testid="sub-handle" />
          </label>

          {/* Periode-valg (kun ved ny + plan finnes) */}
          {!isEdit && plan && plan.periods?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-[var(--cream-soft)] mb-1.5">Periode</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                {plan.periods.map((per) => (
                  <button key={per.id} onClick={() => pickPeriod(per)}
                    className={`p-2 border text-left transition-colors ${f.selected_period_id === per.id ? "border-[var(--magenta)] bg-[var(--magenta)]/10" : "border-white/10 hover:border-white/20"}`}
                    data-testid={`pick-period-${per.id}`}>
                    <div className="text-xs font-fraunces italic text-[var(--cream)]">{per.label}</div>
                    <div className="text-[10px] text-[var(--cream-soft)]">{per.days}d · {per.price} {plan.currency}</div>
                  </button>
                ))}
                <button onClick={pickCustom}
                  className={`p-2 border border-dashed text-left ${f.selected_period_id === "custom" ? "border-[var(--magenta)] bg-[var(--magenta)]/10" : "border-white/20"}`}
                  data-testid="pick-period-custom">
                  <div className="text-xs font-fraunces italic text-[var(--cream)]">Egendefinert</div>
                  <div className="text-[10px] text-[var(--cream-soft)]">Egne dager + pris</div>
                </button>
              </div>
              {f.selected_period_id === "custom" && (
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <label className="block">
                    <span className="text-[9px] uppercase tracking-[0.15em] text-[var(--cream-soft)]">Dager</span>
                    <input type="number" min="1" value={f.custom_days} onChange={(e)=>applyCustom(e.target.value, f.custom_price)}
                      className="input-editorial w-full mt-0.5" data-testid="custom-days" />
                  </label>
                  <label className="block">
                    <span className="text-[9px] uppercase tracking-[0.15em] text-[var(--cream-soft)]">Pris</span>
                    <input type="number" min="0" value={f.custom_price} onChange={(e)=>applyCustom(f.custom_days, e.target.value)}
                      className="input-editorial w-full mt-0.5" data-testid="custom-price" />
                  </label>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            <label className="block col-span-2">
              <span className="text-[10px] uppercase tracking-[0.15em] text-[var(--cream-soft)]">Beløp*</span>
              <input type="number" min="0" value={f.amount} onChange={(e)=>set("amount", e.target.value)}
                className="input-editorial w-full mt-1" data-testid="sub-amount" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.15em] text-[var(--cream-soft)]">Valuta</span>
              <select value={f.currency} onChange={(e)=>set("currency", e.target.value)}
                className="input-editorial w-full mt-1">
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.15em] text-[var(--cream-soft)]">Betalt dato</span>
              <input type="date" value={f.paid_at} onChange={(e)=>set("paid_at", e.target.value)}
                className="input-editorial w-full mt-1" data-testid="sub-paid-at" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.15em] text-[var(--cream-soft)]">Tilgang til</span>
              <input type="date" value={f.access_until} onChange={(e)=>set("access_until", e.target.value)}
                className="input-editorial w-full mt-1" data-testid="sub-access-until" />
            </label>
          </div>

          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.15em] text-[var(--cream-soft)]">Notat / kommentar</span>
            <textarea value={f.subscriber_note} onChange={(e)=>set("subscriber_note", e.target.value)}
              rows={2} className="input-editorial w-full mt-1"
              placeholder="God kunde, foretrekker …" data-testid="sub-note" />
          </label>

          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.15em] text-[var(--cream-soft)]">Påminnelse ved utløp (dager før)</span>
            <input type="number" min="0" max="30" value={f.remind_days_before}
              onChange={(e)=>set("remind_days_before", e.target.value)}
              className="input-editorial w-full mt-1" data-testid="sub-remind" />
          </label>

          {/* Custom påminnelser */}
          <div className="border-t border-white/10 pt-3">
            <div className="flex items-center justify-between mb-2">
              <div className="font-fraunces italic text-[var(--cream)] flex items-center gap-2"><Bell className="h-3.5 w-3.5"/> Egne påminnelser</div>
              <button onClick={addReminder} className="btn-ghost text-xs inline-flex items-center gap-1" data-testid="add-reminder">
                <Plus className="h-3 w-3" /> Ny påminnelse
              </button>
            </div>
            {f.reminders.length === 0 && (
              <div className="text-xs text-[var(--cream-faint)] italic">Ingen påminnelser satt.</div>
            )}
            <div className="space-y-2">
              {f.reminders.map((r, i) => (
                <div key={i} className="grid grid-cols-[130px_1fr_auto] gap-2 items-start">
                  <input type="date" value={r.remind_at} onChange={(e)=>setReminder(i, "remind_at", e.target.value)}
                    className="input-editorial" data-testid={`reminder-date-${i}`} />
                  <input value={r.note} onChange={(e)=>setReminder(i, "note", e.target.value)}
                    placeholder="Husk å sende bilde av …"
                    className="input-editorial" data-testid={`reminder-note-${i}`} />
                  <button onClick={() => rmReminder(i)}
                    className="btn-ghost p-1.5 text-rose-400 hover:text-rose-300 mt-0.5" data-testid={`reminder-remove-${i}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-white/10 flex gap-2 sticky bottom-0 bg-[var(--noir-elev)]">
          <button onClick={onCancel} className="btn-ghost flex-1">Avbryt</button>
          <button onClick={submit} disabled={saving} className="btn-magenta flex-1" data-testid="sub-save">
            {saving ? "Lagrer…" : (isEdit ? "Oppdater" : "Legg til")}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ================================================================ PAGE */
export default function SkaperSosialAbonnenter() {
  const { user, loading } = useAuth();
  const [platforms, setPlatforms] = useState([]);
  const [plans, setPlans] = useState([]);
  const [subs, setSubs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filterPlatform, setFilterPlatform] = useState("");
  const [filterAccount, setFilterAccount] = useState("");
  const [filterPlan, setFilterPlan] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(true);

  // Flow-state
  const [flow, setFlow] = useState({ open: false, step: null, plan: null, platform: null, platformAccount: null, editing: null });

  const openNew = () => setFlow({ open: true, step: "choose", plan: null, platform: null, platformAccount: null, editing: null });
  const openEdit = (sub) => setFlow({ open: true, step: "detail", plan: null, platform: sub.platform, platformAccount: sub.platform_account, editing: sub });
  const closeFlow = () => setFlow({ open: false, step: null, plan: null, platform: null, platformAccount: null, editing: null });

  const load = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (filterPlatform) params.set("platform", filterPlatform);
      if (filterAccount !== "") params.set("platform_account", filterAccount);
      if (filterPlan) params.set("plan_id", filterPlan);
      if (filterStatus) params.set("status", filterStatus);
      if (q.trim()) params.set("q", q.trim());
      const [r1, r2, r3, r4] = await Promise.all([
        api.get(`/me/social-subs?${params.toString()}`),
        api.get(`/me/social-subs/summary`),
        api.get(`/me/social-subs/plans`),
        api.get(`/me/social-subs/meta/platforms`),
      ]);
      setSubs(r1.data.subs || []);
      setSummary(r2.data || null);
      setPlans(r3.data.plans || []);
      setPlatforms(r4.data.platforms || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    const isCreator = user?.is_creator || user?.role === "owner" || user?.role === "admin";
    if (isCreator) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, filterPlatform, filterAccount, filterPlan, filterStatus]);

  const doSave = async (payload) => {
    try {
      if (flow.editing) {
        await api.patch(`/me/social-subs/${flow.editing.id}`, payload);
        toast.success("Oppdatert");
      } else {
        await api.post(`/me/social-subs`, payload);
        toast.success("Registrert");
      }
      closeFlow();
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const doDelete = async (sub) => {
    if (!window.confirm(`Slette ${sub.subscriber_handle} (${sub.platform})?`)) return;
    try {
      await api.delete(`/me/social-subs/${sub.id}`);
      toast.success("Slettet");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filteredBySearch = useMemo(() => {
    if (!q.trim()) return subs;
    const s = q.trim().toLowerCase();
    return subs.filter((d) =>
      (d.subscriber_handle || "").toLowerCase().includes(s) ||
      (d.subscriber_note   || "").toLowerCase().includes(s) ||
      (d.platform_account  || "").toLowerCase().includes(s)
    );
  }, [subs, q]);

  // Available accounts + plans given current filters
  const availableAccounts = useMemo(() => {
    const set = new Set();
    for (const p of plans) if (!filterPlatform || p.platform === filterPlatform) set.add(p.platform_account || "");
    for (const s of subs) if (!filterPlatform || s.platform === filterPlatform) set.add(s.platform_account || "");
    return Array.from(set);
  }, [plans, subs, filterPlatform]);

  const availablePlans = useMemo(() => {
    return plans.filter(p =>
      (!filterPlatform || p.platform === filterPlatform) &&
      (filterAccount === "" || (p.platform_account || "") === filterAccount)
    );
  }, [plans, filterPlatform, filterAccount]);

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  const isCreator = user.is_creator || user.role === "owner" || user.role === "admin";
  if (!isCreator) return <Navigate to="/" replace />;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-6" data-testid="social-subs-page">
        <div className="mb-4 flex items-center gap-3">
          <Link to="/skaper/studio" className="text-[var(--cream-faint)] hover:text-[var(--magenta)] inline-flex items-center gap-1.5">
            <ArrowLeft className="h-4 w-4" /> Skaper-studio
          </Link>
        </div>

        <div className="flex flex-wrap items-end justify-between gap-4 mb-2">
          <h1 className="font-fraunces italic text-3xl sm:text-4xl text-[var(--cream)]">
            Abonnenter (sosiale medier)
          </h1>
          <div className="flex flex-wrap gap-2">
            <Link to="/skaper/sosial-abonnenter/planer" className="btn-ghost inline-flex items-center gap-2" data-testid="subs-plans-btn">
              <Layers className="h-4 w-4" /> Planer
            </Link>
            <button onClick={openNew} className="btn-magenta inline-flex items-center gap-2" data-testid="subs-new-btn">
              <Plus className="h-4 w-4" /> Registrer betaling
            </button>
          </div>
        </div>
        <p className="text-sm text-[var(--cream-soft)] mb-6">
          Manuell oversikt for Snap, Telegram, WhatsApp o.a. der plattformen ikke har innebygd betalingsløsning.
        </p>

        {/* KPI-kort */}
        {summary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="subs-summary">
            <div className="p-4 border border-white/10 bg-[var(--noir-elev)]">
              <div className="flex items-center gap-2 text-[var(--cream-soft)] text-[10px] uppercase tracking-[0.15em] mb-1"><Wallet className="h-3.5 w-3.5" /> Total inntekt</div>
              <div className="font-fraunces italic text-2xl text-[var(--cream)]">{fmtMoney(summary.total_income, "NOK")}</div>
            </div>
            <div className="p-4 border border-white/10 bg-[var(--noir-elev)]">
              <div className="flex items-center gap-2 text-[var(--cream-soft)] text-[10px] uppercase tracking-[0.15em] mb-1"><UsersIcon className="h-3.5 w-3.5" /> Aktive</div>
              <div className="font-fraunces italic text-2xl text-emerald-400">{summary.active_count}</div>
            </div>
            <div className="p-4 border border-white/10 bg-[var(--noir-elev)]">
              <div className="flex items-center gap-2 text-[var(--cream-soft)] text-[10px] uppercase tracking-[0.15em] mb-1"><AlertTriangle className="h-3.5 w-3.5" /> Utløper snart</div>
              <div className="font-fraunces italic text-2xl text-amber-400">{summary.expiring_soon_count}</div>
            </div>
            <div className="p-4 border border-white/10 bg-[var(--noir-elev)]">
              <div className="flex items-center gap-2 text-[var(--cream-soft)] text-[10px] uppercase tracking-[0.15em] mb-1"><Clock className="h-3.5 w-3.5" /> Utløpt</div>
              <div className="font-fraunces italic text-2xl text-rose-400">{summary.expired_count}</div>
            </div>
          </div>
        )}

        {/* Filter-bar */}
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="subs-filter-bar">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--cream-faint)]" />
            <input value={q} onChange={(e)=>setQ(e.target.value)}
              placeholder="Søk abonnent, konto, notat…"
              className="input-editorial w-full pl-9" data-testid="subs-search" />
          </div>
          <select value={filterPlatform} onChange={(e)=>{ setFilterPlatform(e.target.value); setFilterAccount(""); setFilterPlan(""); }}
            className="input-editorial" data-testid="subs-filter-platform">
            <option value="">Alle plattformer</option>
            {platforms.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <select value={filterAccount} onChange={(e)=>{ setFilterAccount(e.target.value); setFilterPlan(""); }}
            className="input-editorial" data-testid="subs-filter-account">
            <option value="">Alle brukernavn</option>
            {availableAccounts.map(a => <option key={a} value={a}>{a || "(uten)"}</option>)}
          </select>
          <select value={filterPlan} onChange={(e)=>setFilterPlan(e.target.value)}
            className="input-editorial" data-testid="subs-filter-plan">
            <option value="">Alle planer</option>
            {availablePlans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <select value={filterStatus} onChange={(e)=>setFilterStatus(e.target.value)}
            className="input-editorial" data-testid="subs-filter-status">
            <option value="all">Alle</option>
            <option value="active">Aktiv</option>
            <option value="expiring_soon">Utløper snart</option>
            <option value="expired">Utløpt</option>
            <option value="lifetime">Livstid</option>
          </select>
        </div>

        {/* Tabell */}
        <div className="border border-white/10 overflow-x-auto" data-testid="subs-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-[var(--cream-soft)] uppercase tracking-[0.12em] text-[10px]">
                <th className="text-left py-2 px-3">Betalt</th>
                <th className="text-left py-2 px-3">Plattform / brukernavn</th>
                <th className="text-left py-2 px-3">Abonnent</th>
                <th className="text-left py-2 px-3">Plan / periode</th>
                <th className="text-right py-2 px-3">Beløp</th>
                <th className="text-left py-2 px-3">Utløp</th>
                <th className="text-left py-2 px-3">Status</th>
                <th className="text-right py-2 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {busy && <tr><td colSpan={8} className="py-6 text-center text-[var(--cream-faint)]">Henter…</td></tr>}
              {!busy && filteredBySearch.length === 0 && (
                <tr><td colSpan={8} className="py-8 text-center text-[var(--cream-faint)] font-fraunces italic">
                  Ingen abonnenter registrert ennå.
                </td></tr>
              )}
              {filteredBySearch.map((s) => (
                <tr key={s.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="py-2 px-3 text-[var(--cream-soft)]">{fmtDate(s.paid_at)}</td>
                  <td className="py-2 px-3">
                    <div className="text-[var(--cream)]">{s.platform}</div>
                    {s.platform_account && <div className="text-[10px] text-[var(--cream-faint)]">{s.platform_account}</div>}
                  </td>
                  <td className="py-2 px-3">
                    <div className="text-[var(--cream)]">{s.subscriber_handle}</div>
                    {s.subscriber_note && <div className="text-[10px] text-[var(--cream-faint)] max-w-[200px] truncate">{s.subscriber_note}</div>}
                    {(s.reminders || []).length > 0 && (
                      <div className="text-[10px] text-amber-400/80 inline-flex items-center gap-1 mt-0.5">
                        <Bell className="h-2.5 w-2.5"/> {s.reminders.length} påminnelse{s.reminders.length === 1 ? "" : "r"}
                      </div>
                    )}
                  </td>
                  <td className="py-2 px-3">
                    {s.plan_name_snapshot && <div className="text-[var(--cream)]">{s.plan_name_snapshot}</div>}
                    {s.period_label_snapshot && <div className="text-[10px] text-[var(--cream-faint)]">{s.period_label_snapshot}</div>}
                  </td>
                  <td className="py-2 px-3 text-right text-[var(--cream)] font-manrope">{fmtMoney(s.amount, s.currency)}</td>
                  <td className="py-2 px-3 text-[var(--cream-soft)]">{fmtDate(s.access_until)}</td>
                  <td className="py-2 px-3">
                    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-[0.1em] border ${STATUS_STYLE[s.status] || ""}`}>
                      {STATUS_LABELS[s.status] || s.status}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(s)} className="btn-ghost p-1.5 mr-1" title="Rediger" data-testid={`subs-edit-${s.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => doDelete(s)} className="btn-ghost p-1.5 text-rose-400 hover:text-rose-300" title="Slett" data-testid={`subs-delete-${s.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Per-plan aggregat */}
        {summary?.per_plan && Object.keys(summary.per_plan).length > 0 && (
          <div className="mt-6 border border-white/10 p-4 bg-[var(--noir-elev)]" data-testid="subs-per-plan">
            <div className="font-fraunces italic text-lg text-[var(--cream)] mb-3">Inntekt per plan</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
              {Object.entries(summary.per_plan).map(([name, s]) => (
                <div key={name} className="flex items-center justify-between border border-white/5 p-2">
                  <div>
                    <div className="text-[var(--cream)]">{name}</div>
                    <div className="text-[10px] text-[var(--cream-faint)]">{s.count} betalinger · {s.active} aktive</div>
                  </div>
                  <div className="text-[var(--cream)] font-manrope">{fmtMoney(s.income, "NOK")}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Top abonnenter */}
        {summary?.top_subscribers?.length > 0 && (
          <div className="mt-6 border border-white/10 p-4 bg-[var(--noir-elev)]">
            <div className="font-fraunces italic text-lg text-[var(--cream)] mb-3">Top-abonnenter (totalt betalt)</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {summary.top_subscribers.map((t, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0">
                  <div className="min-w-0">
                    <div className="text-[var(--cream)] truncate">{t.subscriber_handle}</div>
                    <div className="text-[10px] text-[var(--cream-faint)]">{t.platform} · {t.payments} betalinger · sist {fmtDate(t.last_paid_at)}</div>
                  </div>
                  <div className="text-[var(--cream)] font-manrope shrink-0 ml-3">{fmtMoney(t.total_paid, "NOK")}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Flow modal */}
        {flow.open && flow.step === "choose" && (
          <ChoosePlanStep
            platforms={platforms}
            plans={plans}
            onCancel={closeFlow}
            onPick={(plan) => setFlow((f) => ({ ...f, step: "detail", plan, platform: plan.platform, platformAccount: plan.platform_account || "" }))}
            onCustom={(platform, platformAccount) => setFlow((f) => ({ ...f, step: "detail", plan: null, platform, platformAccount }))}
          />
        )}
        {flow.open && flow.step === "detail" && (
          <DetailStep
            initial={flow.editing}
            plan={flow.plan}
            platform={flow.platform}
            platformAccount={flow.platformAccount}
            onCancel={closeFlow}
            onBack={flow.editing ? null : () => setFlow((f) => ({ ...f, step: "choose", plan: null }))}
            onSave={doSave}
          />
        )}
      </div>
    </Layout>
  );
}
